import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';
import confetti from 'canvas-confetti';

const CelebrationContext = createContext(null);

/**
 * Reads a brand token's live value off the root element so confetti colors
 * follow the current theme. canvas-confetti needs literal color strings, so
 * this must be resolved at fire time rather than baked into a module-level
 * constant.
 */
function tok(name, fallback) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return fallback;
  }
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

/**
 * Brand-only festive palette for course completion (no blue/purple).
 */
function getCourseCompleteColors() {
  return [
    tok('--success', '#57AEA1'),
    tok('--accent-primary', '#3FA37A'),
    tok('--accent-hover', '#4FB88C'),
    tok('--warning', '#C89A4A'),
  ];
}

/**
 * Brand-only palette for the quiz-pass pulse (no blue/purple).
 */
function getQuizPassColors() {
  return [
    tok('--success', '#57AEA1'),
    tok('--accent-primary', '#3FA37A'),
  ];
}

/**
 * Confetti configuration for course completion
 */
const COURSE_COMPLETE_CONFIG = {
  particleCount: 150,
  spread: 70,
  origin: { y: 0.6 },
  duration: 3000,
  waves: 3,
};

/**
 * Confetti configuration for quiz pass - single pulse
 */
const QUIZ_PASS_CONFIG = {
  particleCount: 60,
  spread: 55,
  origin: { y: 0.7 },
};

/**
 * Fire confetti for course completion - multiple waves from both sides
 */
function fireCourseCompleteConfetti() {
  const { particleCount, spread, origin, duration, waves } = COURSE_COMPLETE_CONFIG;
  const colors = getCourseCompleteColors();
  const interval = duration / waves;

  for (let i = 0; i < waves; i++) {
    setTimeout(() => {
      // Fire from left
      confetti({
        particleCount: Math.floor(particleCount / 2),
        angle: 60,
        spread,
        origin: { x: 0, y: origin.y },
        colors,
        startVelocity: 45,
        gravity: 1,
        drift: 0,
        ticks: 300,
        scalar: 1,
      });

      // Fire from right
      confetti({
        particleCount: Math.floor(particleCount / 2),
        angle: 120,
        spread,
        origin: { x: 1, y: origin.y },
        colors,
        startVelocity: 45,
        gravity: 1,
        drift: 0,
        ticks: 300,
        scalar: 1,
      });
    }, i * interval);
  }
}

/**
 * Fire confetti for quiz pass - single pulse from center
 */
function fireQuizPassConfetti() {
  const { particleCount, spread, origin } = QUIZ_PASS_CONFIG;
  const colors = getQuizPassColors();

  confetti({
    particleCount,
    spread,
    origin: { x: 0.5, y: origin.y },
    colors,
    startVelocity: 30,
    gravity: 0.8,
    ticks: 200,
    scalar: 0.9,
  });
}

/**
 * Provider component for celebration context
 */
export function CelebrationProvider({ children }) {
  const [isShowing, setIsShowing] = useState(false);

  /**
   * Celebrate course completion - grand multi-wave effect
   */
  const celebrateCourseComplete = useCallback(() => {
    setIsShowing(true);
    fireCourseCompleteConfetti();

    setTimeout(() => {
      setIsShowing(false);
    }, COURSE_COMPLETE_CONFIG.duration + 500);
  }, []);

  /**
   * Celebrate quiz pass - single pulse effect
   */
  const celebrateQuizPass = useCallback(() => {
    setIsShowing(true);
    fireQuizPassConfetti();

    setTimeout(() => {
      setIsShowing(false);
    }, 1500);
  }, []);

  // Expose to window for easy testing in browser console
  useEffect(() => {
    window.graspd = window.graspd || {};
    window.graspd.celebrateCourseComplete = celebrateCourseComplete;
    window.graspd.celebrateQuizPass = celebrateQuizPass;

    return () => {
      if (window.graspd) {
        delete window.graspd.celebrateCourseComplete;
        delete window.graspd.celebrateQuizPass;
      }
    };
  }, [celebrateCourseComplete, celebrateQuizPass]);

  const value = {
    isShowing,
    celebrateCourseComplete,
    celebrateQuizPass,
  };

  return (
    <CelebrationContext.Provider value={value}>
      {children}
    </CelebrationContext.Provider>
  );
}

/**
 * Hook to access celebration functions
 */
export function useCelebration() {
  const context = useContext(CelebrationContext);
  if (!context) {
    throw new Error('useCelebration must be used within a CelebrationProvider');
  }
  return context;
}

export default CelebrationContext;
